import styled from 'styled-components'
import { ClmmPool, PositionConfigInfo, StructClass, TypeArgument } from '@kunalabs-io/kai'
import { useMemo } from 'react'
import debounce from 'lodash.debounce'

import { Typography } from 'components/Typography/Typography'
import { SelectSwapAggregatorMenu } from './SelectSwapAggregatorMenu'
import { SwapTypeMenu } from './SwapTypeMenu'
import { PercentageInput } from './PercentageInput'
import { LiquidityProvidersMenu } from './LiquidityProvidersMenu'
import { useIsMobile } from 'utils/useIsMobile'
import { PriceSection } from './PriceSection'
import { SwapType } from '../hooks/useWithdrawDetails'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconInfo } from 'components/Icons/InfoIcon'

const DELAY = 500

const Container = styled.div<{ $isExactOut: boolean }>`
  display: flex;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    justify-content: ${p => (p.$isExactOut ? 'flex-start' : 'space-between')};
    ${p => p.$isExactOut && `gap: 12px;`}
  }

  flex-wrap: wrap;
  & > div {
    width: calc(25% - 12px);
  }

  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    justify-content: space-between;
    & > div {
      width: calc(50% - 8px);
      margin-bottom: 16px;
    }
  }
`

const Aggregator = styled(Typography)`
  display: flex;
  align-items: center;
  cursor: pointer;
  svg {
    margin-left: 4px;
  }
`

const ResetToDefault = styled(Typography)`
  text-decoration: underline;
  text-align: right;
  cursor: pointer;
  margin-top: 10px;
`

interface Props {
  configInfo: PositionConfigInfo<string, string, TypeArgument>
  isReversedPair: boolean
  pool?: ClmmPool<StructClass, unknown, string, string>
  slippagePct: number
  handleSlippagePctChange: (value: number) => void
  overestimationPct: number
  handleOverestimationPctChange: (value: number) => void
  handleResetValues: () => void
  swapType: SwapType
  handleSwapTypeChange: (newSwapType: SwapType) => void
  selectedProtocols: Record<string, boolean>
  handleUpdateProtocols: (protocols: Record<string, boolean>) => void
}

const OptionTitle = styled(Typography)`
  display: flex;
  align-items: center;
  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 16px;
      height: 16px;
      & > path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

export const WithdrawDetails = ({
  configInfo,
  isReversedPair,
  pool,
  slippagePct,
  handleSlippagePctChange,
  overestimationPct,
  handleOverestimationPctChange,
  handleResetValues,
  swapType,
  handleSwapTypeChange,
  selectedProtocols,
  handleUpdateProtocols,
}: Props) => {
  const isMobile = useIsMobile()

  const debouncedChangeResetValues = useMemo(() => debounce(handleResetValues, DELAY), [handleResetValues])

  return (
    <div>
      {isMobile && (
        <div style={{ marginBottom: 16 }}>
          <PriceSection
            tokenXSymbol={isReversedPair ? configInfo?.Y.displaySymbol || '' : configInfo?.X.displaySymbol || ''}
            tokenYSymbol={isReversedPair ? configInfo?.X.displaySymbol || '' : configInfo?.Y.displaySymbol || ''}
            currentPrice={
              isReversedPair
                ? pool?.currentPrice().inverted().human.toDP(2).toString()
                : pool?.currentPrice().human.toDP(2).toString()
            }
          />
        </div>
      )}
      <Container $isExactOut={swapType === 'exact-out'}>
        <div>
          <Aggregator variant="caption" color="secondary">
            Aggregator
            <LiquidityProvidersMenu protocols={selectedProtocols} handleUpdateProtocols={handleUpdateProtocols} />
          </Aggregator>
          <SelectSwapAggregatorMenu />
        </div>
        <div>
          <OptionTitle variant="caption" color="secondary">
            Method
            <Tooltip
              content="Choose whether you input the exact amount to swap (Exact In) or the exact amount you want to receive (Exact Out)."
              position="bottom"
              tooltipBoxStyles={isMobile ? { marginLeft: -40 } : {}}
            >
              <IconInfo width={16} height={16} />
            </Tooltip>
          </OptionTitle>
          <SwapTypeMenu swapType={swapType} handleSwapTypeChange={handleSwapTypeChange} />
        </div>
        <div>
          <OptionTitle variant="caption" color="secondary" style={{ marginBottom: 8 }}>
            Slippage
            <Tooltip
              content="The maximum price difference you’ll accept due to market changes during the swap."
              position="bottom"
              tooltipBoxStyles={isMobile ? { marginLeft: 50 } : {}}
            >
              <IconInfo width={16} height={16} />
            </Tooltip>
          </OptionTitle>
          <PercentageInput value={slippagePct} onChange={handleSlippagePctChange} />
        </div>
        {swapType === 'exact-in' && (
          <div>
            <OptionTitle variant="caption" color="secondary" style={{ marginBottom: 8 }}>
              Overestimation
              <Tooltip
                content="Extra buffer added when swapping to ensure enough output for debt repayment. Helps avoid failed transactions due to slippage or router inaccuracy."
                position="bottom"
                tooltipBoxStyles={isMobile ? { marginLeft: -90, marginTop: -50 } : { minWidth: 450, marginLeft: -170 }}
              >
                <IconInfo width={16} height={16} />
              </Tooltip>
            </OptionTitle>
            <PercentageInput value={overestimationPct} onChange={handleOverestimationPctChange} />
          </div>
        )}
      </Container>
      <div onClick={debouncedChangeResetValues}>
        <ResetToDefault variant="caption">Reset to default</ResetToDefault>
      </div>
    </div>
  )
}
